// Email AI API - API integration for AI-powered email generation
// Uses ApiClient with dependency injection pattern

import type { ApiClient } from '@commander/api-client';
import type {
  AiGeneration,
  AiGenerationCreateData,
  AiGenerationCreateResponse,
  BackendAiGeneration,
} from '../types/aiGeneration';

/**
 * Transform backend AI generation response to frontend type
 */
function transformAiGeneration(backend: BackendAiGeneration): AiGeneration {
  return {
    id: backend.id,
    type: backend.type,
    prompt: backend.prompt,
    status: backend.status,
    subject: backend.subject,
    body: backend.body,
    errorMessage: backend.error_message,
    signingUrl: backend.signingUrl ?? null,
    createdAt: new Date(backend.created_at),
    updatedAt: new Date(backend.updated_at),
  };
}

/**
 * Email AI API service factory
 * Provides methods for AI-powered email generation
 */
export function createEmailAiApi(apiClient: ApiClient) {
  return {
    /**
     * Generate email content with AI
     * @param data - Generation request data
     * @returns Generation ID and status
     */
    async generateEmail(data: AiGenerationCreateData): Promise<AiGenerationCreateResponse> {
      const response = await apiClient.post<{ data: AiGenerationCreateResponse }>(
        '/communications/emails/ai',
        data
      );
      return response.data;
    },

    /**
     * Get AI generation result
     * @param generationId - ID of the generation to fetch
     * @returns AI generation result with Lexical JSON body (if completed)
     */
    async getGeneration(generationId: string): Promise<AiGeneration> {
      const response = await apiClient.get<{ data: BackendAiGeneration }>(
        `/communications/emails/ai/${generationId}`
      );
      return transformAiGeneration(response.data);
    },
  };
}
